import React, { useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatTime, getPlanMapPoints } from './routePlannerUtils';

const LIMA_CENTER = [-12.0464, -77.0428];

function markerIcon(point) {
  const size = point.priority ? 34 : 28;
  const label = point.sequence || point.id;
  const background = point.warning ? '#dc2626' : point.unassigned ? '#64748b' : point.color;
  const markerClass = point.warning ? 'route-plan-marker route-plan-marker-warning' : 'route-plan-marker';
  return L.divIcon({
    className: '',
    html: `
      <div class="${markerClass}" style="
        width:${size}px;height:${size}px;border-radius:999px;
        background:${background};
        color:white;display:flex;align-items:center;justify-content:center;
        font-size:11px;font-weight:800;border:2px solid white;
        box-shadow:0 6px 14px rgba(15,23,42,.25);
      ">${label}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((point) => [point.lat, point.lng]));
    map.fitBounds(bounds.pad(0.18), { maxZoom: 13 });
  }, [map, points]);
  return null;
}

function RectangleSelector({ enabled, points, onComplete }) {
  const map = useMap();
  const layerRef = React.useRef(null);
  const startRef = React.useRef(null);
  const rectangleRef = React.useRef(null);

  React.useEffect(() => {
    if (!map || !enabled) return undefined;

    layerRef.current = L.layerGroup().addTo(map);
    map.dragging.disable();
    map.getContainer().style.cursor = 'crosshair';

    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    const clearRectangle = () => {
      layerRef.current?.clearLayers();
      startRef.current = null;
      rectangleRef.current = null;
    };

    const onMouseDown = (event) => {
      if (event.originalEvent.button !== 0) return;
      event.originalEvent.preventDefault();
      startRef.current = event.latlng;
      rectangleRef.current = L.rectangle([event.latlng, event.latlng], {
        color: '#B1336E',
        weight: 2,
        fillOpacity: 0.16,
      }).addTo(layerRef.current);
    };

    const onMouseMove = (event) => {
      if (!startRef.current || !rectangleRef.current) return;
      rectangleRef.current.setBounds([startRef.current, event.latlng]);
    };

    const onMouseUp = (event) => {
      if (event.originalEvent.button !== 0 || !startRef.current || !rectangleRef.current) return;
      event.originalEvent.preventDefault();

      const bounds = L.latLngBounds(startRef.current, event.latlng);
      const selected = points
        .filter((point) => point.stop_id || point.unassigned)
        .filter((point) => bounds.contains(L.latLng(point.lat, point.lng)))
        .map((point) => ({
          type: point.unassigned ? 'unassigned' : 'stop',
          id: point.unassigned ? point.id : point.stop_id,
          delivery_point_id: point.id,
          label: `#${point.id}`,
          district: point.district,
          driver_name: point.driver_name,
        }));

      clearRectangle();
      onComplete(selected);
    };

    map.getContainer().addEventListener('contextmenu', preventContextMenu);
    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      map.getContainer().style.cursor = '';
      map.getContainer().removeEventListener('contextmenu', preventContextMenu);
      map.dragging.enable();
      clearRectangle();
      layerRef.current?.remove();
    };
  }, [enabled, map, onComplete, points]);

  return null;
}

function RoutePlanMap({ plan, preview, drawingMode = false, onToggleDrawing, onMapSelection, className = '' }) {
  const points = useMemo(() => getPlanMapPoints(plan, preview), [plan, preview]);
  const canDraw = Boolean(plan?.id && points.length);

  return (
    <section className={`relative z-0 flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white p-4 ${className}`}>
      <style>
        {`
          @keyframes route-plan-warning-bounce {
            0%, 100% { transform: translateY(0) scale(1); }
            45% { transform: translateY(-6px) scale(1.08); }
          }

          @keyframes route-plan-warning-ring {
            0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, .45), 0 6px 14px rgba(15,23,42,.25); }
            70% { box-shadow: 0 0 0 12px rgba(220, 38, 38, 0), 0 6px 14px rgba(15,23,42,.25); }
            100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0), 0 6px 14px rgba(15,23,42,.25); }
          }

          .route-plan-marker-warning {
            animation:
              route-plan-warning-bounce 1.35s ease-in-out infinite,
              route-plan-warning-ring 1.35s ease-out infinite;
            transform-origin: center bottom;
          }
        `}
      </style>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="font-bold text-slate-900">Mapa operativo</h2>
          {drawingMode && <p className="text-xs font-semibold text-brand-primary">Dibuja un rectangulo sobre las entregas</p>}
        </div>
        <div className="flex items-center gap-2">
          {onToggleDrawing && (
            <button
              onClick={onToggleDrawing}
              disabled={!canDraw}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                drawingMode
                  ? 'bg-brand-primary text-white'
                  : 'border border-slate-200 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              {drawingMode ? 'Cancelar seleccion' : 'Seleccionar en mapa'}
            </button>
          )}
          <span className="text-sm text-slate-500">{points.length} puntos con ubicacion</span>
        </div>
      </div>
      <div className="relative z-0 min-h-[460px] flex-1 overflow-hidden rounded-lg border border-slate-200 xl:min-h-0">
        <MapContainer center={LIMA_CENTER} zoom={11} scrollWheelZoom className="z-0 h-full w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          <RectangleSelector enabled={drawingMode} points={points} onComplete={onMapSelection} />
          {points.map((point) => (
            <Marker key={`${point.unassigned ? 'unassigned' : point.route_id || 'preview'}-${point.id}`} position={[point.lat, point.lng]} icon={markerIcon(point)}>
              <Popup>
                <div className="text-sm">
                  <div className="font-bold">Entrega #{point.id}</div>
                  <div>{point.recipient_name}</div>
                  <div>{point.district || 'Sin distrito'} | Slot {point.slot || 0}</div>
                  {point.unassigned && <div className="font-bold text-slate-600">Sin asignar: {point.unassigned_reason}</div>}
                  {point.driver_name && <div>Driver: {point.driver_name}</div>}
                  {point.sequence && <div>Secuencia: {point.sequence}</div>}
                  {point.eta && <div>ETA: {formatTime(point.eta)}</div>}
                  {point.warning && <div className="font-bold text-red-600">{point.warning}</div>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </section>
  );
}

export default RoutePlanMap;
